import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { GetMyWalletGQL, GetWalletByEmployeeIdGQL } from 'graphql/wallet/wallet.definition';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  private walletValueSource = new BehaviorSubject<number>(0);
  walletValue$ = this.walletValueSource.asObservable();

  // Distingue "saldo zero" de "saldo ainda nao carregado". Sem isso a Loja
  // bloqueia o catalogo inteiro por "pontos insuficientes" enquanto a carteira
  // nao chega.
  private loadedSource = new BehaviorSubject<boolean>(false);
  loaded$ = this.loadedSource.asObservable();

  /** Separado de `loaded`: erro encerra a espera mas nao impede nova tentativa. */
  private fetched = false;

  // "Nao sei o saldo" e diferente de "o saldo e zero". Sem essa distincao, uma
  // carteira que falha ao carregar bloquearia o catalogo inteiro com
  // "pontos insuficientes".
  private unknownSource = new BehaviorSubject<boolean>(false);
  unknown$ = this.unknownSource.asObservable();

  constructor(
    private getMyWalletGQL: GetMyWalletGQL,
    private getWalletByEmployeeIdGQL: GetWalletByEmployeeIdGQL
  ) {}

  get value(): number {
    return this.walletValueSource.getValue();
  }

  get loaded(): boolean {
    return this.loadedSource.getValue();
  }

  updateWalletValue(value: number) {
    this.walletValueSource.next(Number(value) || 0);
    this.fetched = true;
    this.unknownSource.next(false);
    this.loadedSource.next(true);
  }

  get unknown(): boolean {
    return this.unknownSource.getValue();
  }

  /** Carrega o saldo do proprio colaborador. Reaproveita o valor ja carregado. */
  load(): Observable<number> {
    if (this.fetched) {
      return of(this.value);
    }
    return this.refresh();
  }

  /**
   * Sempre vai na rede. Usado depois da troca, quando o saldo mudou.
   *
   * `getMyWallet` resolve pelo token e e a rota correta, mas ainda nao esta em
   * todos os ambientes: onde a API nao tem a query, o servidor responde 400 e
   * caimos em `getWalletByEmployeeId`, que e o caminho que o header ja usa.
   */
  refresh(): Observable<number> {
    // Enquanto a rede nao volta, quem exibe saldo mostra "carregando" em vez do
    // valor antigo - a tela de sucesso rotula esse numero como "Novo saldo".
    this.loadedSource.next(false);
    return this.fetchMyWallet().pipe(
      switchMap((value) => (value === null ? this.fetchWalletByEmployeeId() : of(value))),
      tap((value) => {
        if (value === null) {
          // Nenhum caminho respondeu. Encerra a espera, mas marca o saldo como
          // desconhecido para a tela nao bloquear tudo como "sem pontos", e
          // deixa `fetched` em false para permitir nova tentativa.
          this.unknownSource.next(true);
          this.loadedSource.next(true);
        } else {
          this.updateWalletValue(value);
        }
      }),
      map((value) => (value === null ? this.value : value))
    );
  }

  private fetchMyWallet(): Observable<number | null> {
    return this.getMyWalletGQL
      // silentError: sem carteira a API responde NotFound e o link global abriria
      // um SweetAlert no carregamento da Loja.
      .fetch({}, { fetchPolicy: 'no-cache', context: { silentError: true } })
      .pipe(
        map(({ data }: any) => (data && data.getMyWallet ? Number(data.getMyWallet.value) || 0 : null)),
        catchError(() => of(null))
      );
  }

  private fetchWalletByEmployeeId(): Observable<number | null> {
    const employeeId = this.readEmployeeId();
    if (!employeeId) {
      return of(null);
    }
    return this.getWalletByEmployeeIdGQL
      .fetch({ employeeId }, { fetchPolicy: 'no-cache', context: { silentError: true } })
      .pipe(
        map(({ data }: any) =>
          data && data.getWalletByEmployeeId ? Number(data.getWalletByEmployeeId.value) || 0 : null
        ),
        catchError(() => of(null))
      );
  }

  private readEmployeeId(): string {
    try {
      return localStorage.getItem('employeeId') || '';
    } catch (e) {
      return '';
    }
  }
}
